import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { useLoader } from '../../provider/LoaderProvider';
import vatDeterminationService from '../../service/vatDetermination.service';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../Interface';
import { VatDeterminationReports } from './VatDeterminationReports';
import { VatDeterminationFields } from './VatDeterminationFields';
import masterDataService from '../../service/masterData.service';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import { CategoryOptionType } from '../../config/vatCodes';

export const VatDeterminationPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { showLoader, hideLoader } = useLoader();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteData, setAutoCompleteData] = useState({
    vatCode: [],
  });
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    vatCode: [],
    purchaseVatCode: [],
    salesVatCode: [],
  });

  const def = {
    id: null,
    vatCode: null,
    documentDirection: null,
    bpType: null,
    area: null,
    vatCodeSubject: null,
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .vatCodeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );
    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          vatCode: resp[0],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          purchaseVatCode: generateAutocompleteItems(
            aData.vatCode.filter((vc) => vc.category === CategoryOptionType.B),
            ['code', 'name'],
            'code'
          ),
          salesVatCode: generateAutocompleteItems(
            aData.vatCode.filter((vc) => vc.category === CategoryOptionType.K),
            ['code', 'name'],
            'code'
          ),
          vatCode: generateAutocompleteItems(aData.vatCode, ['code', 'name'], 'code'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();
    initDefaultValues();
    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('vatDetermination');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    vatCode: Yup.string().required(),
    documentDirection: Yup.string().required(),
    bpType: Yup.string().required(),
    area: Yup.string().required(),
    vatCodeSubject: Yup.string().required(),
  });

  const valuesToForm = () => {
    const { id, vatCode, documentDirection, bpType, area, vatCodeSubject } = selectedValues;

    setInitValues({
      id,
      vatCode,
      documentDirection,
      bpType,
      area,
      vatCodeSubject,
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
    };

    vatDeterminationService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
    };

    vatDeterminationService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    vatDeterminationService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="VatDetermination"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <VatDeterminationFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      addField={addField}
                      autoCompleteOptions={autoCompleteOptions}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <VatDeterminationReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autoCompleteOptions={autoCompleteOptions}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
